import { useState, useEffect } from 'react';
import { BiCheck, BiX, BiSave, BiCog, BiEdit } from 'react-icons/bi';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import Alert from '../components/Alert';
import { useCurrentUser } from '../hooks/useCurrentUser';
import settingsService, { Settings as SystemSettings } from '../services/settingsService';

// Helper functions for currency formatting
const penceToDecimals = (pence: number): string => {
  return (pence / 100).toFixed(2)
}

const decimalsToPence = (decimals: string): number => {
  const parsed = parseFloat(decimals)
  return isNaN(parsed) ? 0 : Math.round(parsed * 100)
}

const formatCurrencyInput = (value: string): string => {
  // Remove any non-numeric characters except decimal point
  const cleaned = value.replace(/[^\d.]/g, '')

  // Split by decimal point
  const parts = cleaned.split('.')

  // If more than one decimal point, keep only the first
  if (parts.length > 2) {
    return parts[0] + '.' + parts.slice(1).join('')
  }

  // Limit to 2 decimal places
  if (parts[1] && parts[1].length > 2) {
    parts[1] = parts[1].substring(0, 2)
  }

  return parts.join('.')
}

const formatCurrencyForSave = (value: string): string => {
  // Convert to decimal format, adding .00 if it's a whole number
  const cleaned = value.replace(/[^\d.]/g, '')
  const parsed = parseFloat(cleaned)

  if (isNaN(parsed)) return '1.00'

  // If it's a whole number, add .00
  if (Number.isInteger(parsed)) {
    return parsed.toFixed(2)
  }

  // If it has decimals, ensure 2 decimal places
  return parsed.toFixed(2)
}

// Validation schema for settings
const settingsSchema = Yup.object().shape({
  defaultAgencySeatCost: Yup.string()
    .required('Agency seat cost is required')
    .test('is-valid-currency', 'Must be £1.00 or more', value => {
      if (!value) return false

      // Use formatCurrencyForSave to get the proper decimal format
      const formattedValue = formatCurrencyForSave(value)
      const numValue = parseFloat(formattedValue)

      if (isNaN(numValue)) return false
      return numValue >= 1.00
    }),
  defaultBasePlanCost: Yup.string()
    .required('Base plan cost is required')
    .test('is-valid-currency', 'Must be £1.00 or more', value => {
      if (!value) return false

      // Use formatCurrencyForSave to get the proper decimal format
      const formattedValue = formatCurrencyForSave(value)
      const numValue = parseFloat(formattedValue)

      if (isNaN(numValue)) return false
      return numValue >= 1.00
    }),
  defaultStandaloneSubscriptionCost: Yup.string()
    .required('Standalone subscription cost is required')
    .test('is-valid-currency', 'Must be £1.00 or more', value => {
      if (!value) return false

      // Use formatCurrencyForSave to get the proper decimal format
      const formattedValue = formatCurrencyForSave(value)
      const numValue = parseFloat(formattedValue)

      if (isNaN(numValue)) return false
      return numValue >= 1.00
    }),
  defaultTrialPeriod: Yup.number()
    .required('Trial period is required')
    .min(1, 'Trial period must be at least 1 day')
    .max(365, 'Trial period cannot exceed 365 days')
})

const Settings = () => {
  const { user } = useCurrentUser()
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState<'success' | 'error' | ''>('');
  const [isEditing, setIsEditing] = useState(false);
  const [settings, setSettings] = useState<SystemSettings | null>(null);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const systemSettings = await settingsService.getSettings();
      setSettings(systemSettings);
    } catch (err: any) {
      console.error('Error fetching settings:', err);
      setError('Failed to load settings. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="animate-pulse">
          <div className="h-8 bg-slate-200 rounded w-1/4 mb-6"></div>
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-20 bg-slate-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <div className="mt-2 text-sm text-red-700">
                <p>{error}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Check if user is admin
  const isAdmin = user?.roles?.includes('admin') || false;

  if (!isAdmin) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">Access Denied</h3>
              <div className="mt-2 text-sm text-yellow-700">
                <p>You don't have permission to access the settings page.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!settings) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-slate-800">No Data</h3>
              <div className="mt-2 text-sm text-slate-700">
                <p>No settings data available.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-slate-900 dark:text-white">System Settings</h1>
        <p className="text-slate-600 dark:text-slate-400 mt-2">
          Manage system-wide settings and configurations.
        </p>
      </div>

      {message && (
        <Alert
          type={messageType as 'success' | 'error'}
          message={message}
          onClose={() => setMessage('')}
        />
      )}

      <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-6">
        <Formik
          initialValues={{
            defaultAgencySeatCost: penceToDecimals(settings.defaultAgencySeatCost),
            defaultBasePlanCost: penceToDecimals(settings.defaultBasePlanCost),
            defaultStandaloneSubscriptionCost: penceToDecimals(settings.defaultStandaloneSubscriptionCost),
            defaultTrialPeriod: settings.defaultTrialPeriod
          }}
          validationSchema={settingsSchema}
          enableReinitialize={true}
          onSubmit={async (values, { setSubmitting, setStatus }) => {
            try {
              setStatus(null)
              setMessage('')
              setMessageType('')

              const updatedSettings = await settingsService.updateSettings({
                defaultAgencySeatCost: decimalsToPence(formatCurrencyForSave(values.defaultAgencySeatCost)),
                defaultBasePlanCost: decimalsToPence(formatCurrencyForSave(values.defaultBasePlanCost)),
                defaultStandaloneSubscriptionCost: decimalsToPence(formatCurrencyForSave(values.defaultStandaloneSubscriptionCost)),
                defaultTrialPeriod: values.defaultTrialPeriod
              })

              setSettings(updatedSettings)
              setIsEditing(false)
              setMessage('Settings updated successfully!')
              setMessageType('success')

              setTimeout(() => {
                setMessage('')
                setMessageType('')
              }, 3000)
            } catch (err: any) {
              console.error('Error updating settings:', err)
              setMessage(err?.message || 'Failed to update settings. Please try again.')
              setMessageType('error')
            } finally {
              setSubmitting(false)
            }
          }}
        >
          {({ values, errors, touched, handleSubmit, isSubmitting, setFieldValue, resetForm }) => (
            <Form>
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-xl font-semibold text-slate-900 dark:text-white">System Configuration</h2>
                  <p className="text-slate-600 dark:text-slate-400 mt-1">
                    Configure default pricing and trial settings for the entire system.
                  </p>
                </div>
                <div className="flex space-x-3">
                  {isEditing ? (
                    <>
                      <button
                        type="button"
                        onClick={() => {
                          setIsEditing(false)
                          setMessage('')
                          setMessageType('')
                          resetForm()
                        }}
                        disabled={isSubmitting}
                        className="inline-flex items-center space-x-2 px-3 py-2 text-sm font-medium text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors disabled:opacity-50"
                      >
                        <BiX size={16} />
                        <span>Cancel</span>
                      </button>
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="inline-flex items-center space-x-2 px-3 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:bg-slate-400 disabled:cursor-not-allowed rounded-lg transition-colors disabled:opacity-50"
                      >
                        {isSubmitting ? (
                          <>
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                            <span>Saving...</span>
                          </>
                        ) : (
                          <>
                            <BiCheck size={16} />
                            <span>Save</span>
                          </>
                        )}
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      onClick={() => {
                        setIsEditing(true)
                        setMessage('')
                        setMessageType('')
                      }}
                      className="inline-flex items-center space-x-2 px-3 py-2 text-sm font-medium text-slate-600 dark:text-slate-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                    >
                      <BiEdit size={16} />
                      <span>Edit</span>
                    </button>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Default Agency Seat Cost
                  </label>
                  <div className="relative">
                    <Field name="defaultAgencySeatCost">
                      {({ field, meta }) => (
                        <div className={`flex border rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-primary-500 focus-within:border-transparent ${isEditing
                          ? meta.touched && meta.error
                            ? 'border-red-300 dark:border-red-600'
                            : 'border-slate-300 dark:border-slate-600'
                          : 'border-slate-200 dark:border-slate-700'
                          }`}>
                          <div className={`flex items-center px-3 ${isEditing
                            ? 'bg-white dark:bg-slate-700'
                            : 'bg-slate-50 dark:bg-slate-800'
                            } border-r border-slate-300 dark:border-slate-600`}>
                            <span className="text-sm text-slate-600 dark:text-slate-400">£</span>
                          </div>
                          <input
                            {...field}
                            type="text"
                            value={isEditing ? values.defaultAgencySeatCost : penceToDecimals(settings.defaultAgencySeatCost)}
                            onChange={(e) => {
                              if (isEditing) {
                                const formattedValue = formatCurrencyInput(e.target.value)
                                setFieldValue('defaultAgencySeatCost', formattedValue)
                              }
                            }}
                            disabled={!isEditing}
                            placeholder="14.99"
                            className={`flex-1 px-3 py-2 bg-transparent focus:outline-none text-sm ${isEditing
                              ? 'text-slate-900 dark:text-white'
                              : 'text-slate-700 dark:text-slate-300 cursor-not-allowed'
                              }`}
                          />
                          <div className={`flex items-center px-3 ${isEditing
                            ? 'bg-white dark:bg-slate-700'
                            : 'bg-slate-50 dark:bg-slate-800'
                            } border-l border-slate-300 dark:border-slate-600`}>
                            <span className="text-sm text-slate-500 dark:text-slate-400">GBP</span>
                          </div>
                        </div>
                      )}
                    </Field>
                  </div>
                  {isEditing && errors.defaultAgencySeatCost && touched.defaultAgencySeatCost && (
                    <p className="text-red-500 text-sm mt-1">{errors.defaultAgencySeatCost}</p>
                  )}
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                    Default cost per seat for agencies
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Default Base Plan Cost
                  </label>
                  <div className="relative">
                    <Field name="defaultBasePlanCost">
                      {({ field, meta }) => (
                        <div className={`flex border rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-primary-500 focus-within:border-transparent ${isEditing
                          ? meta.touched && meta.error
                            ? 'border-red-300 dark:border-red-600'
                            : 'border-slate-300 dark:border-slate-600'
                          : 'border-slate-200 dark:border-slate-700'
                          }`}>
                          <div className={`flex items-center px-3 ${isEditing
                            ? 'bg-white dark:bg-slate-700'
                            : 'bg-slate-50 dark:bg-slate-800'
                            } border-r border-slate-300 dark:border-slate-600`}>
                            <span className="text-sm text-slate-600 dark:text-slate-400">£</span>
                          </div>
                          <input
                            {...field}
                            type="text"
                            value={isEditing ? values.defaultBasePlanCost : penceToDecimals(settings.defaultBasePlanCost)}
                            onChange={(e) => {
                              if (isEditing) {
                                const formattedValue = formatCurrencyInput(e.target.value)
                                setFieldValue('defaultBasePlanCost', formattedValue)
                              }
                            }}
                            disabled={!isEditing}
                            placeholder="49.99"
                            className={`flex-1 px-3 py-2 bg-transparent focus:outline-none text-sm ${isEditing
                              ? 'text-slate-900 dark:text-white'
                              : 'text-slate-700 dark:text-slate-300 cursor-not-allowed'
                              }`}
                          />
                          <div className={`flex items-center px-3 ${isEditing
                            ? 'bg-white dark:bg-slate-700'
                            : 'bg-slate-50 dark:bg-slate-800'
                            } border-l border-slate-300 dark:border-slate-600`}>
                            <span className="text-sm text-slate-500 dark:text-slate-400">GBP</span>
                          </div>
                        </div>
                      )}
                    </Field>
                  </div>
                  {isEditing && errors.defaultBasePlanCost && touched.defaultBasePlanCost && (
                    <p className="text-red-500 text-sm mt-1">{errors.defaultBasePlanCost}</p>
                  )}
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                    Default base plan cost for agencies
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Default Standalone Subscription Cost
                  </label>
                  <div className="relative">
                    <Field name="defaultStandaloneSubscriptionCost">
                      {({ field, meta }) => (
                        <div className={`flex border rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-primary-500 focus-within:border-transparent ${isEditing
                          ? meta.touched && meta.error
                            ? 'border-red-300 dark:border-red-600'
                            : 'border-slate-300 dark:border-slate-600'
                          : 'border-slate-200 dark:border-slate-700'
                          }`}>
                          <div className={`flex items-center px-3 ${isEditing
                            ? 'bg-white dark:bg-slate-700'
                            : 'bg-slate-50 dark:bg-slate-800'
                            } border-r border-slate-300 dark:border-slate-600`}>
                            <span className="text-sm text-slate-600 dark:text-slate-400">£</span>
                          </div>
                          <input
                            {...field}
                            type="text"
                            value={isEditing ? values.defaultStandaloneSubscriptionCost : penceToDecimals(settings.defaultStandaloneSubscriptionCost)}
                            onChange={(e) => {
                              if (isEditing) {
                                const formattedValue = formatCurrencyInput(e.target.value)
                                setFieldValue('defaultStandaloneSubscriptionCost', formattedValue)
                              }
                            }}
                            disabled={!isEditing}
                            placeholder="29.99"
                            className={`flex-1 px-3 py-2 bg-transparent focus:outline-none text-sm ${isEditing
                              ? 'text-slate-900 dark:text-white'
                              : 'text-slate-700 dark:text-slate-300 cursor-not-allowed'
                              }`}
                          />
                          <div className={`flex items-center px-3 ${isEditing
                            ? 'bg-white dark:bg-slate-700'
                            : 'bg-slate-50 dark:bg-slate-800'
                            } border-l border-slate-300 dark:border-slate-600`}>
                            <span className="text-sm text-slate-500 dark:text-slate-400">GBP</span>
                          </div>
                        </div>
                      )}
                    </Field>
                  </div>
                  {isEditing && errors.defaultStandaloneSubscriptionCost && touched.defaultStandaloneSubscriptionCost && (
                    <p className="text-red-500 text-sm mt-1">{errors.defaultStandaloneSubscriptionCost}</p>
                  )}
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                    Default cost for standalone subscriptions
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Default Trial Period (days)
                  </label>
                  <Field name="defaultTrialPeriod">
                    {({ field, meta }) => (
                      <input
                        {...field}
                        type="number"
                        value={isEditing ? values.defaultTrialPeriod : settings.defaultTrialPeriod}
                        onChange={(e) => {
                          if (isEditing) {
                            setFieldValue('defaultTrialPeriod', parseInt(e.target.value) || 0)
                          }
                        }}
                        disabled={!isEditing}
                        min="1"
                        max="365"
                        placeholder="30"
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm ${isEditing
                          ? meta.touched && meta.error
                            ? 'border-red-300 dark:border-red-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white'
                            : 'border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white'
                          : 'border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-700 dark:text-slate-300 cursor-not-allowed'
                          }`}
                      />
                    )}
                  </Field>
                  {isEditing && errors.defaultTrialPeriod && touched.defaultTrialPeriod && (
                    <p className="text-red-500 text-sm mt-1">{errors.defaultTrialPeriod}</p>
                  )}
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                    Default trial period for new subscriptions
                  </p>
                </div>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default Settings; 